import { DOCUMENT } from '@angular/common';
import { APP_BASE_HREF } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';

import { MatTabChangeEvent } from '@angular/material/tabs';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  DataAccessExhibitorsFacade,
  DataAccessFairsFacade,
  DataAccessPostsFacade,
  DataAccessPostsPageFacade,
  DataAccessProductsFacade,
  DataAccessProfessionsFacade,
  Fair,
} from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade, SliderConfig } from '@planone/bau365/data-access-screensize';
import { BreadcrumbHelper } from '@planone/bau365/feature-breadcrumb';
import { LinkClickFacade, ShareFacade, TabChangeFacade } from '@planone/bau365/feature-tracking';
import {
  ContactPerson,
  DescribedImage,
  Download,
  Exhibitor,
  Link,
  Post,
  Product,
  Profession,
} from '@planone/bau365/util-fair-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { SWIPER_INTERACTION_EMITTER, SwiperFacade, SwiperInteractionEvent } from '@planone/shared/data-access-swiper';
import {
  BreadcrumbElement,
  ModalBottomsheetHelper,
  ModalBottomsheetType,
  ShareButtonsHelper,
} from '@planone/shared/ui-elements';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { ImageProviders, ImageUrlPipe } from '@planone/shared/util-pipelines';
import { filterIsDefined } from '@planone/shared/util-rxjs-operator-type-helper';
import { ArticleGenerator, BreadcrumbData, BreadcrumbGenerator } from '@planone/shared/util-structured-data';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

export interface StructuredDataPost {
  '@context'?: string;
  '@type'?: string;
  headline?: string;
  image?: string[];
  datePublished?: string;
  dateModified?: string;
  author?:
    | {
        '@type'?: string;
        name?: string;
        url?: string;
      }
    | {
        '@type'?: string;
        name?: string;
        url?: string;
      }[];
  publisher?: {
    '@type'?: string;
    name?: string;
    logo?: {
      '@type'?: string;
      url?: string;
    };
  };
}

@Component({
  selector: 'bau365-page-post-details',
  templateUrl: './page-post-details.component.html',
  styleUrls: ['./page-post-details.component.scss'],
})
export class PagePostDetailsComponent implements OnInit, OnDestroy {
  post$: Observable<Post | undefined>;
  postLoading$: Observable<boolean | undefined>;
  postError$: Observable<string | undefined>;
  products$: Observable<Product[] | undefined>;
  posts$: Observable<Post[] | undefined>;
  professions$: Observable<Profession[] | undefined>;
  fair$: Observable<Fair | undefined>;
  size$: Observable<string | undefined>;
  cardSliderConfig$: Observable<SliderConfig | null | undefined>;
  exhibitor$: Observable<Exhibitor | undefined>;
  fairId$: Observable<string>;

  exhibitor?: Exhibitor = undefined;
  post?: Post = undefined;
  socialShareUrl: string | undefined;
  size: string | undefined;
  breadcrumbElements: BreadcrumbElement[] | undefined;
  emailSubject = localizedMeta['shareEmailSubject'];
  emailBody = localizedMeta['shareEmailBody'];

  private unsubscribe: Subject<void> = new Subject<void>();
  private exhibitorLoaded: Subject<void> = new Subject<void>();

  structuredDataPost: StructuredDataPost = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
  };

  @ViewChild('shareButtonsTitle') public shareButtonsTitle: TemplateRef<never> | null | undefined;
  @ViewChild('shareButtons') public shareButtons: TemplateRef<never> | null | undefined;

  constructor(
    private PostsFacade: DataAccessPostsFacade,
    private PostsPageFacade: DataAccessPostsPageFacade,
    ProductsFacade: DataAccessProductsFacade,
    ProfessionsFacade: DataAccessProfessionsFacade,
    ExhibitorsFacade: DataAccessExhibitorsFacade,
    private FairsFacade: DataAccessFairsFacade,
    ScreensizeFacade: DataAccessScreensizeFacade,
    private trackingTabChangeFacade: TabChangeFacade,
    private trackingShareFacade: ShareFacade,
    private trackingLinkClickFacade: LinkClickFacade,
    private SwiperFacade: SwiperFacade,
    private activatedRoute: ActivatedRoute,
    private renderer2: Renderer2,
    private structuredDataArticleGenerator: ArticleGenerator,
    private structuredDataBreadcrumbGenerator: BreadcrumbGenerator,
    private metaService: MetaService,
    private shareButtonsHelper: ShareButtonsHelper,
    private modalBottomsheetHelper: ModalBottomsheetHelper,
    private sanitizer: DomSanitizer,
    private breadcrumbHelper: BreadcrumbHelper,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(SWIPER_INTERACTION_EMITTER) private swiperInteractionEmitter: BehaviorSubject<SwiperInteractionEvent>,
    public TogglesFacade: DataAccessTogglesFacade
  ) {
    this.metaService.setTitle(localizedMeta['titlePostDetails'], [$localize`:@@pageMeta-titles-loading: Lädt...`]);

    this.post$ = PostsFacade.post$;
    this.posts$ = PostsFacade.postPosts$;
    this.postLoading$ = PostsFacade.postLoading$;
    this.postError$ = PostsFacade.postError$;
    this.fair$ = FairsFacade.fair$;
    this.size$ = ScreensizeFacade.size$;
    this.cardSliderConfig$ = ScreensizeFacade.cardSliderConfig$;
    this.professions$ = this.post$.pipe(
      map((post) => post?.professions),
      filterIsDefined(),
      switchMap((professions) => {
        return (this.professions$ = ProfessionsFacade.getProfessionsById(
          professions.map((profession) => profession.id)
        ));
      })
    );
    this.products$ = this.post$.pipe(
      map((post) => post?.products),
      filterIsDefined(),
      switchMap((products) => {
        return (this.products$ = ProductsFacade.getProductsById(products));
      })
    );
    this.exhibitor$ = this.post$.pipe(
      map((post) => post?.exhibitor_id),
      filterIsDefined(),
      switchMap((exhibitor_id) => {
        return (this.exhibitor$ = ExhibitorsFacade.getExhibitorById(exhibitor_id));
      })
    );
    this.fairId$ = FairsFacade.fairId$;
  }

  ngOnInit() {
    this.FairsFacade.fairConfig$
      .pipe(combineLatestWith(this.FairsFacade.isBauInsights$), takeUntil(this.unsubscribe))
      .subscribe(([fair, isBauInsights]) => {
        this.metaService.removeOpenGraphMetaTags();

        if (isBauInsights) {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitlePostPage'],
            localizedMeta['linkPreviewDescriptionPostCompanyPage'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        } else {
          this.metaService.addOpenGraphMetaTags(
            localizedMeta['linkPreviewTitlePostPage'],
            localizedMeta['linkPreviewDescriptionPostExhibitorPage'],
            this.document.URL,
            this.baseHref + 'assets/link-preview-img.png',
            [fair.name]
          );
        }
      });

    this.swiperInteractionEmitter.pipe(takeUntil(this.unsubscribe)).subscribe((event: SwiperInteractionEvent) => {
      this.swiperInteraction(event);
    });

    this.size$.pipe(takeUntil(this.unsubscribe)).subscribe((size) => (this.size = size));

    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      this.PostsPageFacade.pageView(params['id'], params['fair_id']);
      this.PostsFacade.pageOpened(params['id'], params['fair_id']);
    });

    combineLatest([this.post$, this.exhibitor$])
      .pipe(withLatestFrom(this.FairsFacade.fairConfig$), takeUntil(this.unsubscribe))
      .subscribe(([[post, exhibitor], fair]) => {
        if (post?.exhibitor_id && exhibitor) {
          this.exhibitor = exhibitor;
          this.post = post;

          this.emailBody += $localize`:@@pagePost-emailShare-entityType-post:Beitrag ${this.post.headline}`;

          this.exhibitorLoaded.next();
          this.exhibitorLoaded.complete();

          this.socialShareUrl = this.shareButtonsHelper.buildShareUrlFromPath(
            'fairs/' + post.fair_id + '/posts/' + post.id
          );

          const breadcrumbData: BreadcrumbData = this.breadcrumbHelper.prepareForPost(fair, exhibitor, post);

          this.breadcrumbElements = breadcrumbData.elements;

          this.structuredDataBreadcrumbGenerator.validateBreadcrumbStructuredData(
            this.renderer2,
            breadcrumbData.structuredData,
            this.document.head
          );

          this.structuredDataPost.publisher = {
            '@type': 'Organization',
          };

          if (this.exhibitor?.display_name || this.exhibitor?.name) {
            this.structuredDataPost.publisher.name = this.exhibitor?.display_name ?? this.exhibitor?.name;
          }

          if (this.exhibitor?.logo?.resource) {
            this.structuredDataPost.publisher.logo = {
              '@type': 'ImageObject',
              url: new ImageUrlPipe(this.sanitizer).transform(
                this.exhibitor?.logo?.resource,
                this.exhibitor?.logo?.provider
              ),
            };
          }

          if (post?.authors && post?.authors.length > 0) {
            let authors = '';

            post?.authors.forEach(
              (author, index) =>
                (authors = authors + this.getAuthorName(author) + (index + 1 < post?.authors.length ? ', ' : ''))
            );

            this.structuredDataPost.author = {
              '@type': 'Person',
              name: authors,
            };
          }

          this.structuredDataArticleGenerator.setTag(this.renderer2, this.structuredDataPost, this.document.head);
        }
      });

    this.post$.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      this.structuredDataPost.headline = params?.headline;
      this.structuredDataPost.dateModified = params?.published_at;
      this.structuredDataPost.datePublished = params?.published_at;

      if (params?.gallery && params?.gallery.length > 0) {
        if (typeof this.structuredDataPost.image === 'undefined') {
          this.structuredDataPost.image = [];
        }

        params?.gallery.forEach((image: DescribedImage) => {
          this.structuredDataPost.image?.push(
            new ImageUrlPipe(this.sanitizer).transform(image.resource, image.provider)
          );
        });
      }

      this.structuredDataArticleGenerator.setTag(this.renderer2, this.structuredDataPost, this.document.head);
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();

    this.metaService.removeOpenGraphMetaTags();
    this.structuredDataArticleGenerator.removeTag(this.renderer2, this.document.head);
    this.structuredDataBreadcrumbGenerator.removeTag(this.renderer2, this.document.head);
  }

  getAuthorName(author: ContactPerson) {
    return (
      (author.title ? author.title + ' ' : '') +
      author.first_name +
      ((author.title || author.first_name ? ' ' : '') + author.name)
    );
  }

  trackDownload(element: EventTarget | null, download: Download) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.post && this.exhibitor) {
        this.PostsPageFacade.trackedItemClicked({
          htmlElement: element,
          linkText: download.name ?? download.file_name ?? download.path,
          linkType: 'DOWNLOAD',
          exhibitor: this.exhibitor,
          post: this.post,
          download: download,
          context: this.exhibitor.fair_id,
          pageType: 'post-details',
        });

        this.trackingLinkClickFacade.trigger({
          entityType: 'POST',
          linkType: 'DOWNLOAD',
          linkText: download.name ?? download.file_name ?? download.path,
          context: this.exhibitor.fair_id ?? fairId,
          pageType: 'post-details',
          eventTarget: element ?? undefined,
        });
      }
    });
  }

  trackBasicClick(element: EventTarget | null, linkType: string, linkText?: string, contactPerson?: ContactPerson) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.post && this.exhibitor) {
        this.PostsPageFacade.trackedItemClicked({
          htmlElement: element,
          linkType: linkType,
          exhibitor: this.exhibitor,
          post: this.post,
          context: this.post.fair_id,
          pageType: 'post-details',
          ...(linkText && { linkText: linkText }),
          ...(contactPerson && { contactPerson: contactPerson }),
        });

        this.trackingLinkClickFacade.trigger({
          entityType: 'POST',
          linkType: linkType,
          linkText: linkText,
          context: this.exhibitor.fair_id ?? fairId,
          pageType: 'post-details',
          eventTarget: element ?? undefined,
          ...(contactPerson && { contactPerson: contactPerson }),
        });
      }
    });
  }

  trackClick(element: EventTarget | null, linkType: string, link?: Link, contactPerson?: ContactPerson) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.post && this.exhibitor) {
        this.PostsPageFacade.trackedItemClicked({
          htmlElement: element,
          linkType: linkType,
          exhibitor: this.exhibitor,
          post: this.post,
          context: this.post.fair_id,
          pageType: 'post-details',
          ...(link?.text && { linkText: link?.text }),
          ...(contactPerson && { contactPerson: contactPerson }),
        });

        this.trackingLinkClickFacade.trigger({
          entityType: 'POST',
          linkType: linkType,
          linkText: link?.text,
          context: this.exhibitor.fair_id ?? fairId,
          pageType: 'post-details',
          eventTarget: element ?? undefined,
          ...(contactPerson && { contactPerson: contactPerson }),
        });
      }
    });
  }

  openShareButtonDialog(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.post) {
        this.PostsPageFacade.shareDialogOpened('POST', 'post-details', fairId, this.post);

        this.trackingShareFacade.open({
          entityType: 'POST',
          pageType: 'post-details',
          context: fairId,
        });
      }
    });

    this.modalBottomsheetHelper.openDialog(
      ModalBottomsheetType.share,
      this.shareButtonsTitle,
      this.shareButtons,
      this.size,
      {
        analyticsEntityType: 'POST',
        analyticsPageType: 'post-details',
        analyticsContext: [],
      }
    );
  }

  shareButtonClick(network: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.post && this.exhibitor) {
        this.PostsPageFacade.shareButtonClicked(network, this.post, this.exhibitor.name, 'post-details', fairId);

        this.trackingShareFacade.click({
          entityType: 'POST',
          pageType: 'post-details',
          context: fairId,
          network: network,
        });
      }
    });
  }

  tabChange(event: MatTabChangeEvent) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      this.PostsPageFacade.tabClicked({
        tabLabel: event.tab.ariaLabel,
        pageType: 'post-details',
        context: this.post?.fair_id ?? this.exhibitor?.fair_id ?? fairId,
        exhibitor: this.exhibitor,
        post: this.post,
      });

      this.trackingTabChangeFacade.trigger({
        context: this.post?.fair_id ?? this.exhibitor?.fair_id ?? fairId,
        pageType: 'post-details',
        tabLabel: event.tab.ariaLabel,
        entityType: 'POST',
      });
    });
  }

  swiperInteraction(event: SwiperInteractionEvent) {
    const slide = (this.post?.hero_image ? [this.post?.hero_image] : []).concat(this.post?.gallery ?? [])[event.index];
    const count = (this.post?.hero_image ? [this.post?.hero_image] : []).concat(this.post?.gallery ?? []).length;

    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (slide) {
        this.SwiperFacade.interaction({
          interaction_type: event.type,
          image_resource: slide.resource,
          image_provider: <ImageProviders>slide.provider,
          image_description: slide.description,
          image_position: event.index + 1,
          context: this.post?.fair_id ?? this.exhibitor?.fair_id ?? fairId,
          page_type: 'post-details',
          exhibitor: this.exhibitor,
          post: this.post,
          image_count: count,
        });
      }
    });
  }
}
